<template>
    <div class="post-card" :class="{'is-hidden-post': post.hidden}">
        <!-- Staff-only banner on hidden posts (participants never receive them) -->
        <div class="hidden-banner" v-if="post.hidden">
            <i class="fas fa-eye-slash mr-2"></i>Hidden from the group — only staff can see this.
        </div>

        <div class="columns is-mobile is-gapless post-header">
            <div class="column is-narrow">
                <div class="avatar" v-if="post.author_photo_file">
                    <img :src="apiUrl + 'app/user/photo/' + post.author_photo_file"/>
                </div>
                <!-- Roger-authored posts surface as the company, not a person -->
                <div class="avatar is-company" v-else-if="!post.user_id">
                    <i class="fas fa-tennis-ball"></i>
                </div>
                <div class="avatar" v-else>
                    <i class="fas fa-user"></i>
                </div>
            </div>
            <div class="column ml-3">
                <div>
                    <strong>{{ post.author_name }}</strong>
                    <b-tag type="is-primary" size="is-small" class="ml-2" v-if="post.is_staff_post">
                        <i class="fas fa-star mr-1"></i>Staff
                    </b-tag>
                    <b-tag type="is-warning" size="is-small" class="ml-2" v-if="post.announcement_type === 'schedule'">
                        <i class="fas fa-calendar-exclamation mr-1"></i>Schedule Update
                    </b-tag>
                </div>
                <div class="is-size-7 has-text-grey">{{ post.created_at | moment('from') }}</div>
            </div>
            <div class="column is-narrow" v-if="canModerate || isAuthor">
                <b-dropdown position="is-bottom-left" aria-role="menu">
                    <template #trigger>
                        <b-button size="is-small" type="is-text"><i class="fas fa-ellipsis-h"></i></b-button>
                    </template>
                    <b-dropdown-item aria-role="menuitem" v-if="canModerate && !post.hidden" @click="hide(1)">
                        <i class="fas fa-eye-slash fa-fw mr-2"></i>Hide from group
                    </b-dropdown-item>
                    <b-dropdown-item aria-role="menuitem" v-if="canModerate && post.hidden" @click="hide(0)">
                        <i class="fas fa-eye fa-fw mr-2"></i>Unhide
                    </b-dropdown-item>
                    <b-dropdown-item aria-role="menuitem" class="has-text-danger" v-if="canModerate || isAuthor" @click="remove">
                        <i class="fas fa-trash fa-fw mr-2"></i>Delete
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <!-- Photo posts lead with the image; the text is a caption under it.
             A single photo runs full width, several form a grid. -->
        <template v-if="hasPhotos">
            <!-- A lone video plays right in the feed; photos open the lightbox. -->
            <div class="photo-hero is-video mt-3" v-if="post.photos.length === 1 && isVideo(post.photos[0])">
                <video controls playsinline preload="metadata" :src="videoUrl(post.photos[0])"></video>
                <!-- Lone videos never open the lightbox, so the download
                     action lives on the hero itself. -->
                <a class="download-badge" :href="downloadUrl(post.photos[0])" title="Download">
                    <i class="fas fa-download"></i>
                </a>
            </div>
            <div class="photo-hero mt-3" v-else-if="post.photos.length === 1" @click="lightbox = post.photos[0]">
                <img :src="photoUrl(post.photos[0], 'feed')"/>
            </div>
            <div class="photo-grid mt-3" v-else>
                <div class="photo-cell" v-for="photo in post.photos" :key="photo.id" @click="lightbox = photo">
                    <template v-if="isVideo(photo)">
                        <video preload="metadata" muted playsinline :src="videoUrl(photo)"></video>
                        <span class="play-badge"><i class="fas fa-play"></i></span>
                    </template>
                    <img v-else :src="photoUrl(photo, 'thumb')"/>
                </div>
            </div>
            <div class="photo-caption" v-if="post.body">{{ post.body }}</div>
        </template>

        <!-- Text posts. body_format 'html' is only ever written by the Roger
             compose (staff Quill) — participant bodies are always 'text', so
             v-html never renders participant input. Same trust model as the
             Quill trips.app_* sections. -->
        <template v-else>
            <div class="post-body content" v-if="post.body && post.body_format === 'html'" v-html="post.body"></div>
            <div class="post-body" v-else-if="post.body">{{ post.body }}</div>
        </template>

        <poll-block
            v-if="post.options && post.options.length"
            :post="post"
            :reg-id="regId"
            :is-staff="isStaff"
            @changed="$emit('changed')"
            class="mt-4"
        />

        <div class="post-footer mt-4">
            <a class="footer-action" :class="{'is-active': post.my_reaction}" @click="toggleReaction">
                <i :class="post.my_reaction ? 'fas fa-heart' : 'far fa-heart'"></i>
                <span class="ml-1" v-if="post.reaction_count">{{ post.reaction_count }}</span>
            </a>
            <!-- No count here — the thread below already shows the comments
                 themselves and the "View all X" link carries the number. -->
            <a class="footer-action ml-5" @click="$refs.thread.toggleComposer()">
                <i class="far fa-comment"></i>
                <span class="ml-1 is-size-7">{{ post.channel === 'announcement' ? 'Reply' : 'Comment' }}</span>
            </a>
        </div>

        <!-- Wall posts show their first three comments inline; announcements
             collapse to a "View replies" link. The input opens from the
             Reply/Comment action above. -->
        <comment-thread
            ref="thread"
            :post="post"
            :reg-id="regId"
            :is-staff="isStaff"
            @changed="$emit('changed')"
            class="mt-4"
        />

        <b-modal v-model="lightboxOpen">
            <div v-if="lightbox">
                <p class="image" v-if="isVideo(lightbox)">
                    <video controls autoplay playsinline :src="videoUrl(lightbox)" class="lightbox-video"></video>
                </p>
                <p class="image" v-else>
                    <img :src="photoUrl(lightbox, 'feed')"/>
                </p>
                <div class="lightbox-footer">
                    <p class="lightbox-caption" v-if="post.body">{{ post.body }}</p>
                    <a class="lightbox-download" :href="downloadUrl(lightbox)">
                        <i class="fas fa-download mr-2"></i>Download
                    </a>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import {mapGetters} from 'vuex';
    import {client as http, url} from '@/http_client';
    import PollBlock from './PollBlock.vue';
    import CommentThread from './CommentThread.vue';

    export default {
        name: 'PostCard',
        components: {PollBlock, CommentThread},
        props: {
            post: {type: Object, required: true},
            regId: {type: Number, required: true},
            isStaff: {type: Boolean, default: false}
        },
        data: function() {
            return {
                apiUrl: url,
                lightbox: null
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'token']),
            isAuthor: function() {
                return this.post.user_id === this.currentUser.id;
            },
            hasPhotos: function() {
                return !!(this.post.photos && this.post.photos.length);
            },
            canModerate: function() {
                return this.isStaff;
            },
            lightboxOpen: {
                get: function() { return !!this.lightbox; },
                set: function(v) { if (!v) this.lightbox = null; }
            }
        },
        methods: {
            photoUrl: function(photo, size) {
                return `${this.apiUrl}app/experience/${this.regId}/photo/${photo.file}/${size}?auth=${this.token}`;
            },
            isVideo: function(photo) {
                return !!photo.file_mime && photo.file_mime.indexOf('video/') === 0;
            },
            // #t=0.001 nudges the browser to render the first frame as the
            // preview — videos have no server-generated poster (no ffmpeg).
            videoUrl: function(photo) {
                return this.photoUrl(photo, 'feed') + '#t=0.001';
            },
            // Server answers with Content-Disposition: attachment (original
            // file, uploader's filename), so a plain href saves instead of
            // navigating.
            downloadUrl: function(photo) {
                return `${this.apiUrl}app/experience/${this.regId}/photo/${photo.file}?download&auth=${this.token}`;
            },
            toggleReaction: function() {
                const base = `/app/experience/${this.regId}/posts/${this.post.id}/reaction`;
                const request = this.post.my_reaction
                    ? http.delete(base, {force: true})
                    : http.post(base, {}, {force: true});
                request.then(() => this.$emit('changed'));
            },
            hide: function(hidden) {
                http.put(`/app/experience/${this.regId}/posts/${this.post.id}/hide`, {hidden}, {force: true})
                    .then(() => this.$emit('changed'));
            },
            remove: function() {
                this.$buefy.dialog.confirm({
                    title: 'Delete Post',
                    message: 'Are you sure you want to delete this post? This can\'t be undone.',
                    confirmText: 'Delete',
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: () => {
                        http.delete(`/app/experience/${this.regId}/posts/${this.post.id}`, {force: true})
                            .then(() => this.$emit('changed'));
                    }
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .post-card {
        background-color: white;
        border-radius: 0.3rem;
        padding: 1rem;
        margin-bottom: 1rem;

        &.is-hidden-post {
            opacity: 0.75;
            border: 1px dashed #b5b5b5;
        }

        .hidden-banner {
            background-color: #fffaeb;
            border: 1px solid #ffe08a;
            border-radius: 0.3rem;
            font-size: 0.8rem;
            padding: 0.4rem 0.8rem;
            margin-bottom: 0.8rem;
        }

        .post-header {
            align-items: center;

            .avatar {
                width: 42px;
                height: 42px;
                border-radius: 50%;
                overflow: hidden;
                border: 1px solid #e7e7e7;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #f5f5f5;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                i {
                    color: #bebebe;
                    font-size: 1.2rem;
                }

                &.is-company {
                    background-color: #628b21;

                    i {
                        color: white;
                    }
                }
            }
        }

        .post-body {
            margin-top: 0.8rem;
            white-space: pre-wrap;
            word-break: break-word;

            // Quill-authored bodies bring their own block markup — the
            // pre-wrap that plain-text posts need would double the spacing.
            &.content {
                white-space: normal;

                ::v-deep p:not(:last-child) {
                    margin-bottom: 0.5em;
                }
            }
        }

        .photo-hero {
            cursor: pointer;
            border-radius: 0.4rem;
            overflow: hidden;
            background-color: #f0f0f0;

            img {
                width: 100%;
                max-height: 480px;
                object-fit: cover;
                display: block;
            }

            &.is-video {
                cursor: default;
                background-color: black;
                position: relative;

                video {
                    width: 100%;
                    max-height: 480px;
                    display: block;
                }

                .download-badge {
                    position: absolute;
                    top: 0.5rem;
                    right: 0.5rem;
                    width: 2rem;
                    height: 2rem;
                    border-radius: 50%;
                    background-color: rgba(0, 0, 0, 0.55);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;

                    &:hover {
                        background-color: rgba(0, 0, 0, 0.8);
                    }
                }
            }
        }

        .photo-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
            gap: 0.4rem;

            .photo-cell {
                cursor: pointer;
                border-radius: 0.3rem;
                overflow: hidden;
                aspect-ratio: 1;
                background-color: #f0f0f0;
                position: relative;

                img,
                video {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }

                .play-badge {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 2.4rem;
                    height: 2.4rem;
                    border-radius: 50%;
                    background-color: rgba(0, 0, 0, 0.55);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    pointer-events: none;
                }
            }
        }

        .photo-caption {
            margin-top: 0.5rem;
            font-size: 0.9rem;
            color: #666;
            white-space: pre-wrap;
            word-break: break-word;
        }

        .lightbox-video {
            width: 100%;
            max-height: 80vh;
            display: block;
            background-color: black;
        }

        .lightbox-footer {
            background-color: rgba(0, 0, 0, 0.7);
            padding: 0.6rem 1rem;
            display: flex;
            align-items: flex-start;
            gap: 1rem;

            .lightbox-caption {
                flex: 1;
                color: white;
                font-size: 0.95rem;
                white-space: pre-wrap;
                word-break: break-word;
            }

            .lightbox-download {
                margin-left: auto;
                white-space: nowrap;
                color: white;
                font-size: 0.9rem;

                &:hover {
                    color: #dbdbdb;
                }
            }
        }

        .post-footer {
            border-top: 1px solid #f0f0f0;
            padding-top: 0.6rem;

            .footer-action {
                color: #7a7a7a;
                font-size: 0.95rem;

                &.is-active {
                    color: #e0245e;
                }

                &:hover {
                    color: #628b21;
                }
            }
        }
    }
</style>
